import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import './ProjectsList.css';
import ProjectCard from './ProjectCard/ProjectCard';
import { useProject } from '../../../contexts/ProjectContext';
import { useEC2InstanceStatus } from '../../../contexts/EC2InstanceStatusContext';
import RefreshButton from '../../RefreshButton/RefreshButton';
import useCachedApi from '../../../hooks/useCachedApi';
import { getProjectActivity, resolveUserEmail } from '../../../utils/projectActivity';
import { getPinnedProjects, setProjectPinned, comparePinnedFirst } from '../../../utils/projectPins';

const FILTER_OPTIONS = [
  { value: 'all', label: 'Show All' },
  { value: 'rsa-model', label: 'RSA Model' },
  { value: 'rsa-existing', label: 'RSA Existing' },
  { value: 'visualization', label: 'Visualization' },
];

const SORT_OPTIONS = [
  { value: 'Name|Project Name|ASC', label: 'Project Name' },
  { value: 'Modified|Last update|DESC', label: 'Last update' },
  { value: 'ModelModified|Last model update|DESC', label: 'Last model update' },
  { value: 'LastViewed|Last Viewed|DESC', label: 'Last Viewed' },
  { value: 'UserRole|Role|ASC', label: 'Role' },
  { value: 'Size|Project Size|DESC', label: 'Project Size' },
];

/**
 * Sort keys that mean "recently active" rather than a fixed project attribute.
 * Both read the same recorded timestamp today; they diverge later, when triggers
 * other than "opened" start feeding "Last update" only.
 */
const ACTIVITY_SORT_KEYS = new Set(['Modified', 'LastViewed']);

/**
 * Coerce a date-ish value to a Date, or null if it isn't one.
 *
 * Needed because these fields are Dates when they come straight from
 * projectService, but ISO strings when they come from the sessionStorage cache
 * (JSON round-trip). Comparing a Date against a string returns false in both
 * directions, so a mixed sort silently collapses to "no order at all".
 */
const toActivityDate = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getProjectsCacheKey = (currentUser) => {
  const email =
    currentUser?.attributes?.email ||
    currentUser?.email ||
    currentUser?.Email ||
    sessionStorage.getItem('userEmail') ||
    'anonymous';
  return `projectsListCache:${String(email).trim().toLowerCase()}`;
};

const readProjectsCache = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.projects) ? parsed.projects : [];
  } catch (error) {
    console.warn('Failed to read projects list cache:', error);
    sessionStorage.removeItem(cacheKey);
    return [];
  }
};

const writeProjectsCache = (cacheKey, projectsToCache) => {
  try {
    const payload = {
      projects: projectsToCache,
      cachedAt: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to persist projects list cache:', error);
  }
};

const ProjectsList = ({ projects = [], loading, onProjectSelect, onViewProject, onRefresh, user, customRedirectPaths = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'rsa-model', 'rsa-existing', 'visualization'
  const [sortBy, setSortBy] = useState('Modified'); // 'Name', 'Modified', 'ModelModified', 'LastViewed', 'UserRole', 'Size'
  const [sortOrder, setSortOrder] = useState('DESC'); // 'ASC', 'DESC'
  const [sortOptionValue, setSortOptionValue] = useState('Modified|Last update|DESC');
  const [openDropdown, setOpenDropdown] = useState(null); // null | 'filter' | 'sort'
  const [isRefreshing, setIsRefreshing] = useState(false);
  const projectsCacheKey = useMemo(() => getProjectsCacheKey(user), [user]);
  const [cachedProjects, setCachedProjects] = useState(() => readProjectsCache(getProjectsCacheKey(user)));
  const [fetchProjects, setExistingProjectName] = useState("");
  const filterDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  // Mobile: the toolbar is hidden until the bottom bar's search icon summons
  // it (the shell dispatches this window event — it has no other coupling to
  // this screen). Opening focuses the input in the same gesture.
  const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const onToggle = () => {
      setMobileToolbarOpen((v) => {
        const next = !v;
        if (next) {
          setTimeout(() => searchInputRef.current?.focus(), 60);
        }
        return next;
      });
    };
    window.addEventListener('rsa:mobile-search-toggle', onToggle);
    return () => window.removeEventListener('rsa:mobile-search-toggle', onToggle);
  }, []);

  // Bottom bar's refresh icon. Ref indirection so the mount-once listener
  // always calls the latest handleRefresh closure.
  const handleRefreshRef = useRef(null);
  useEffect(() => {
    const onMobileRefresh = () => handleRefreshRef.current?.();
    window.addEventListener('rsa:mobile-refresh', onMobileRefresh);
    return () => window.removeEventListener('rsa:mobile-refresh', onMobileRefresh);
  }, []);
  
  // Use project context utilities
  const { formatDate, formatFileSize, isProjectExpired, canViewProject, canAccessProjectFiles } = useProject();
  
  // Use EC2 instance status context for polling
  const { startPolling, stopPolling, updateProjects } = useEC2InstanceStatus();
  
  // Memoize startPolling and stopPolling to prevent infinite loops
  const startPollingRef = useRef(startPolling);
  const stopPollingRef = useRef(stopPolling);
  
  useEffect(() => {
    startPollingRef.current = startPolling;
    stopPollingRef.current = stopPolling;
  }, [startPolling, stopPolling]);

  // Switch cache bucket when user changes.
  useEffect(() => {
    setCachedProjects(readProjectsCache(projectsCacheKey));
  }, [projectsCacheKey]);

  // Keep cache synced with fresh projects payload.
  useEffect(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      setCachedProjects(projects);
      writeProjectsCache(projectsCacheKey, projects);
    }
  }, [projects, projectsCacheKey]);

  const projectsToRender = useMemo(() => {
    if (Array.isArray(projects) && projects.length > 0) {
      return projects;
    }
    return cachedProjects;
  }, [projects, cachedProjects]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openDropdown === 'filter' && filterDropdownRef.current && !filterDropdownRef.current.contains(e.target) ||
        openDropdown === 'sort' && sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdown]);

  // Debounce search term with shorter delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 50); // Reduced to 50ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized filtering and sorting for better performance
  // Read once per mount. Projects.jsx swaps currentView when a project opens, so
  // this component unmounts and remounts on the way back — a just-recorded open is
  // always picked up without any cross-component signalling.
  const activityMap = useMemo(() => getProjectActivity(resolveUserEmail(user)), [user]);

  // Pins, unlike activity, are toggled while this component is MOUNTED — so they
  // live in state, not a mount-time memo, and the grid re-sorts on the click.
  // Seeded lazily so the correct order is there on first paint (no reshuffle).
  const [pinnedIds, setPinnedIds] = useState(() => getPinnedProjects(resolveUserEmail(user)));

  // The SORT reads this lagging copy, never pinnedIds directly.
  //
  // Pinning used to re-sort on the click, which teleported the card out from under
  // the cursor — and whatever slid into the vacated slot looked like your card with
  // the pin not applied, so a reflexive second click pinned the wrong project. So the
  // sort is held still while the pointer is still ON the card that changed: the icon
  // and badge update immediately (they read pinnedIds), but nothing moves. Step off
  // that card and it syncs, and the FLIP effect below animates the settle.
  //
  // The trigger is deliberately the CARD, not the grid. Releasing on grid-leave was
  // the first attempt and felt broken: the grid is the full width of the content area
  // and as tall as every card, so the pointer can wander inside it for many seconds
  // without crossing an edge — the settle appeared to fire at random, ~7s after the
  // click. Card-leave keeps the same protection and costs one mouse movement.
  const [sortPinnedIds, setSortPinnedIds] = useState(pinnedIds);
  const pointerInGridRef = useRef(false);
  // Card whose pin changed while the pointer was on it, and which is therefore
  // holding the sort back. Null when nothing is pending.
  const pendingPinCardRef = useRef(null);

  // `user` resolves asynchronously; re-seed once the real identity lands so we are
  // not stuck showing the 'anonymous' bucket. Same reasoning as the `user` dep on
  // Projects.jsx's recordProjectOpened effect.
  // Costs one extra render on mount (a fresh Set with identical contents is still
  // a new identity). That is accepted rather than guarded — the alternative is a
  // content-comparison that runs on every user change to save a single re-render.
  useEffect(() => {
    const fresh = getPinnedProjects(resolveUserEmail(user));
    setPinnedIds(fresh);
    setSortPinnedIds(fresh);
  }, [user]);

  // Derives `next` from pinnedIds rather than using a setState updater: the write
  // to localStorage is a side effect, and StrictMode double-invokes updaters in dev.
  const handleTogglePin = useCallback((projectId) => {
    const email = resolveUserEmail(user);
    const next = !pinnedIds.has(String(projectId));
    const updated = setProjectPinned(projectId, email, next);
    setPinnedIds(updated);
    // Reached without hovering the grid (keyboard, or a synthetic click) — there is
    // no cursor to protect, so re-sort at once rather than waiting for a mouseleave
    // that may never come.
    if (pointerInGridRef.current) {
      pendingPinCardRef.current = String(projectId);
    } else {
      pendingPinCardRef.current = null;
      setSortPinnedIds(updated);
    }
  }, [pinnedIds, user]);

  const handleGridPointerEnter = useCallback(() => {
    pointerInGridRef.current = true;
  }, []);

  // mouseover bubbles, so one handler on the grid sees every card. Fires on each
  // move between elements; the ref checks below make the no-op case two compares.
  const handleGridPointerOver = useCallback((event) => {
    pointerInGridRef.current = true;
    const pending = pendingPinCardRef.current;
    if (!pending) return;
    const card = event.target.closest?.('[data-project-id]');
    // Still on the card that changed — keep holding.
    if (card?.dataset.projectId === pending) return;
    pendingPinCardRef.current = null;
    setSortPinnedIds(pinnedIds);
  }, [pinnedIds]);

  // Backstop: leaving the grid entirely (straight off a card's edge to the sidebar)
  // may not produce a mouseover on another card first.
  const handleGridPointerLeave = useCallback(() => {
    pointerInGridRef.current = false;
    pendingPinCardRef.current = null;
    setSortPinnedIds(pinnedIds);
  }, [pinnedIds]);

  const filteredProjects = useMemo(() => {
    if (!projectsToRender || !Array.isArray(projectsToRender)) {
      return [];
    }

    let filtered = [...projectsToRender];

    // Apply search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(project =>
        project.Name.toLowerCase().includes(searchLower) ||
        project.Plan.toLowerCase().includes(searchLower) ||
        project.UserRole.toLowerCase().includes(searchLower) ||
        // Project ID — the value the card surfaces as `.role` ("ID: 2437").
        // Stringified because Id arrives as a number; substring rather than
        // exact so a partial "24" narrows the list the way the other fields do.
        String(project.Id ?? '').includes(searchLower)
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(project => project.ProjectType === filterType);
    }

    // "Last update" / "Last Viewed" sort by activity. The server still sends the
    // creation date for these fields, so fold in the locally-recorded open time and
    // keep whichever is newer. max() rather than a straight override so this stays
    // correct once the backend starts sending a real activity timestamp: whichever
    // side is fresher wins, and nothing here needs changing.
    const sortValueFor = (project) => {
      if (!ACTIVITY_SORT_KEYS.has(sortBy)) return project[sortBy];

      // Everything on an activity key leaves here as a Date, so the sort below
      // never compares a Date against a string.
      const baseDate = toActivityDate(project[sortBy]);
      const recordedDate = toActivityDate(activityMap[String(project.Id)]);

      if (!recordedDate) return baseDate ?? new Date(0);
      if (!baseDate) return recordedDate;
      return recordedDate > baseDate ? recordedDate : baseDate;
    };

    // Apply sorting
    filtered.sort((a, b) => {
      // Pinned projects form a block above everything, under every sort mode.
      // MUST come before the ASC/DESC branch below, or an ascending sort would
      // invert this and drop the pinned block to the bottom.
      const pinCmp = comparePinnedFirst(sortPinnedIds, a, b);
      if (pinCmp !== 0) return pinCmp;

      let aValue = sortValueFor(a);
      let bValue = sortValueFor(b);

      // Handle date fields
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'ASC') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [projectsToRender, debouncedSearchTerm, filterType, sortBy, sortOrder, activityMap, sortPinnedIds]);

  // FLIP: animate cards from where they were to where they now are, so a pinned
  // card can be followed to the top instead of teleporting.
  //
  // Positions come from offsetLeft/offsetTop, NOT getBoundingClientRect — the
  // previous frame's values are recorded in an earlier layout pass, and rect
  // coordinates are viewport-relative, so a scroll in between would corrupt every
  // delta. Offsets are relative to the offsetParent and survive scrolling.
  const gridRef = useRef(null);
  const cardOffsetsRef = useRef(new Map());
  const renderedOrderRef = useRef(null);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      // Grid unmounted (empty/loading state) — drop stale geometry so the next
      // mount animates from its own first paint rather than from old positions.
      cardOffsetsRef.current = new Map();
      renderedOrderRef.current = null;
      return;
    }

    const cards = Array.from(grid.querySelectorAll('[data-project-id]'));
    const order = cards.map((card) => card.dataset.projectId).join(',');
    const previousOffsets = cardOffsetsRef.current;
    const orderChanged = renderedOrderRef.current !== null && renderedOrderRef.current !== order;
    renderedOrderRef.current = order;

    // Measure the settled layout BEFORE touching any transform, and record it for
    // the next reorder. Reading after inverting would capture the offset instead.
    const offsets = new Map();
    const moved = [];
    cards.forEach((card) => {
      const id = card.dataset.projectId;
      const left = card.offsetLeft;
      const top = card.offsetTop;
      offsets.set(id, { left, top });

      const previous = previousOffsets.get(id);
      if (!previous) return;
      const dx = previous.left - left;
      const dy = previous.top - top;
      // Sub-pixel drift from grid reflow is not a move worth animating.
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      moved.push({ card, dx, dy });
    });
    cardOffsetsRef.current = offsets;

    if (!orderChanged || moved.length === 0) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Invert with transitions OFF. `.project-card` carries `transition: all 0.3s
    // ease`, so without suppressing it the card would animate INTO the offset —
    // sliding backwards away from its destination — instead of out of it.
    moved.forEach(({ card, dx, dy }) => {
      card.style.transition = 'none';
      card.style.transform = `translate(${dx}px, ${dy}px)`;
    });

    // One forced reflow for the whole batch commits the inverted positions.
    void grid.offsetWidth;

    moved.forEach(({ card }) => {
      const handleEnd = (event) => {
        if (event.propertyName !== 'transform') return;
        // Hand `transition: all` back to the stylesheet, or the card's hover
        // box-shadow and border transitions stay dead for the rest of the session.
        card.style.transition = '';
        card.style.transform = '';
        card.removeEventListener('transitionend', handleEnd);
      };
      card.addEventListener('transitionend', handleEnd);
      card.style.transition = 'transform 320ms cubic-bezier(0.2, 0, 0.2, 1)';
      card.style.transform = '';
    });
  }, [filteredProjects]);
  
  // Start/stop polling when projects change.
  // Only poll visualization-type projects — RSA projects never have an EC2
  // instance in UnrealProjectsInstances, so polling them would just hit the
  // backend, return "No instance name found for project", and consume budget
  // we'd rather spend on projects whose dot the user is actually waiting on.
  useEffect(() => {
    if (projectsToRender && projectsToRender.length > 0) {
      const projectIds = projectsToRender
        .filter(p => {
          const t = String(p?.ProjectType || '').toLowerCase();
          return t !== 'rsa-existing' && t !== 'rsa-exiting'
              && t !== 'rsa-model'    && t !== 'rsaa-model';
        })
        .map(p => String(p.Id));
      if (projectIds.length > 0) {
        startPollingRef.current(projectIds);
      } else {
        stopPollingRef.current();
      }
    } else {
      stopPollingRef.current();
    }

    // Cleanup on unmount
    return () => {
      stopPollingRef.current();
    };
  }, [projectsToRender]); // Removed startPolling and stopPolling from dependencies to prevent infinite loop
  
  // Note: We don't need to update projects on filteredProjects change
  // The main projects list is already being polled via the projects useEffect above
  // Filtering/sorting is just a view concern, not a polling concern

  const handleSortChange = useCallback((value) => {
    const [field, label, order] = value.split('|');
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };
  handleRefreshRef.current = handleRefresh;


  if (loading && projectsToRender.length === 0) {
    return (
      <div className="projects-list-loading">
        <div className="projects-list-loading-spinner"></div>
        Loading projects...
      </div>
    );
  }

  return (
    <div className="projects-list-container">
      {/* Header Controls. mobile-open: summoned by the bottom bar's search
          icon; hidden otherwise on phones (desktop unaffected). */}
      <div className={`projects-header${mobileToolbarOpen ? ' mobile-open' : ''}`}>
        <div className="projects-controls">
          {/* Search */}
          <div className="search-container">
            <input
              type="search"
              placeholder="Search by name or ID"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              ref={searchInputRef}
            />
            <i className="fas fa-search search-icon"></i>
          </div>

          {/* Filters - custom dropdowns so open list has dark background */}
          <div className="filter-controls">
            {/* filter-group--filter has no CSS of its own today; it is kept as
                the symmetric partner of filter-group--sort (which the phone
                toolbar uses to hide the sort dropdown) so the two siblings
                stay identifiable in devtools and either one can be targeted
                without first having to add a class. */}
            <div className="filter-group filter-select-wrap filter-group--filter" ref={filterDropdownRef}>
              <button
                type="button"
                className="filter-select filter-select-trigger"
                onClick={() => setOpenDropdown(openDropdown === 'filter' ? null : 'filter')}
                aria-haspopup="listbox"
                aria-expanded={openDropdown === 'filter'}
                aria-label="Filter projects"
              >
                {/* The visible "Show:" label is gone — the funnel says which kind
                    of control this is, the value says what's applied. aria-label
                    above keeps the name for screen readers. */}
                <i className="fas fa-filter filter-select-icon" aria-hidden="true" />
                <span>{FILTER_OPTIONS.find(o => o.value === filterType)?.label ?? 'Show All'}</span>
                <i className={`fas fa-chevron-down filter-select-chevron ${openDropdown === 'filter' ? 'open' : ''}`} />
              </button>
              {openDropdown === 'filter' && (
                <div className="filter-select-dropdown-panel" role="listbox">
                  {FILTER_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={filterType === opt.value}
                      className={`filter-select-option ${filterType === opt.value ? 'selected' : ''}`}
                      onClick={() => { setFilterType(opt.value); setOpenDropdown(null); }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-group filter-select-wrap filter-group--sort" ref={sortDropdownRef}>
              <button
                type="button"
                className="filter-select filter-select-trigger"
                onClick={() => setOpenDropdown(openDropdown === 'sort' ? null : 'sort')}
                aria-haspopup="listbox"
                aria-expanded={openDropdown === 'sort'}
                aria-label="Sort projects"
              >
                {/* Carries more weight than the filter icon: "Last update" reads
                    as a filter value without it. This is what tells the two
                    neighbouring pills apart now the labels are gone. */}
                <i className="fas fa-arrow-down-wide-short filter-select-icon" aria-hidden="true" />
                <span>{SORT_OPTIONS.find(o => o.value === sortOptionValue)?.label ?? 'Last update'}</span>
                <i className={`fas fa-chevron-down filter-select-chevron ${openDropdown === 'sort' ? 'open' : ''}`} />
              </button>
              {openDropdown === 'sort' && (
                <div className="filter-select-dropdown-panel" role="listbox">
                  {SORT_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      role="option"
                      aria-selected={sortOptionValue === opt.value}
                      className={`filter-select-option ${sortOptionValue === opt.value ? 'selected' : ''}`}
                      onClick={() => {
                        setSortOptionValue(opt.value);
                        handleSortChange(opt.value);
                        setOpenDropdown(null);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>



          {/* View Mode Toggle */}
          <div className="view-controls">
            <RefreshButton
              onRefresh={handleRefresh}
              loading={isRefreshing}
              size="medium"
              variant="secondary"
              showText={false}
              className="projects-refresh-btn"
            />
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className="projects-content gallery">
        {isRefreshing ? (
          <div className="projects-list-loading">
            <div className="loading-spinner">
            </div>
            <p>Refreshing projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="no-projects">
            <i className="fas fa-folder-open"></i>
            <h3>No projects found</h3>
            <p>
              {debouncedSearchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'No projects available at the moment.'}
            </p>
          </div>
        ) : (
          <div
            ref={gridRef}
            className={`projects-grid gallery ${filteredProjects.length >= 3 ? 'fill-width' : ''}`.trim()}
            onMouseEnter={handleGridPointerEnter}
            onMouseOver={handleGridPointerOver}
            onMouseLeave={handleGridPointerLeave}
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.Id}
                project={project}
                onProjectSelect={onProjectSelect}
                onViewProject={onViewProject}
                formatDate={formatDate}
                user={user}
                customRedirectPath={customRedirectPaths[project.Id]}
                cardIndex={index}
                isPinned={pinnedIds.has(String(project.Id))}
                onTogglePin={handleTogglePin}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
